import type { AutocompleteApi as InternalAutocompleteApi, AutocompleteSource as InternalAutocompleteSource, AutocompleteState as InternalAutocompleteState } from '@algolia/autocomplete-core';
import { createAutocomplete } from '@algolia/autocomplete-core';
import { parseAlgoliaHitSnippet } from '@algolia/autocomplete-preset-algolia';
import type { Hit } from '@algolia/client-search';
import { CaretRightIcon, Cross2Icon, ExclamationTriangleIcon, ExternalLinkIcon, MagnifyingGlassIcon } from '@radix-ui/react-icons';
import { Badge, Box, Code, Flex, IconButton, Kbd, Slot, Text, TextField, Tooltip, VisuallyHidden } from '@workos-inc/design-system';
import algoliasearch from 'algoliasearch/lite';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { composeRefs, DismissableLayer } from 'radix-ui/internal';
import * as React from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import { isExternalUrl } from 'src/utils/is-external-url';
import { DEFAULT_LANGUAGE } from './code-block-language-provider';
import { FadedBottomScroll } from './faded-bottom-scroll';
import { HttpMethodBadge } from './http-method-badge';
const ALGOLIA_APP_ID = '5V4QVNID73';
const searchClient = algoliasearch(ALGOLIA_APP_ID, process.env.NEXT_PUBLIC_ALGOLIA_API_KEY || '');
type ApiReferenceItem = Hit<{
  language: string;
  title: string;
  slug: string;
  type?: string;
  sendsEmail?: boolean;
}>;
const SUPPORTED_LEVELS = ['lvl0', 'lvl1', 'lvl2', 'lvl3', 'lvl4'] as const;
type LevelContentType = (typeof SUPPORTED_LEVELS)[number];
type ContentType = LevelContentType | 'content';
type DocsItem = Hit<{
  type: ContentType;
  url: string;
  url_without_anchor: string;
  anchor: string;
  hierarchy: {
    lvl0: string;
    lvl1: string;
    lvl2: string | null;
    lvl3: string | null;
    lvl4: string | null;
  };
  content: string | null;
}>;
type SearchItem = ApiReferenceItem | DocsItem;
type AutocompleteState = InternalAutocompleteState<SearchItem>;
type AutocompleteApi = InternalAutocompleteApi<SearchItem, React.BaseSyntheticEvent, React.MouseEvent, React.KeyboardEvent>;
type ApiReferenceSource = InternalAutocompleteSource<ApiReferenceItem>;
type DocsSource = InternalAutocompleteSource<DocsItem>;
type AutocompleteSource = ApiReferenceSource | DocsSource;
const isMac = () => {
  return typeof window !== 'undefined' && navigator.platform.toLowerCase().includes('mac');
};
const getSearchShortcutDisplay = () => {
  return isMac() ? '⌘K' : '^K';
};
const getSearchShortcutMessage = (shortcutDisplay: string) => {
  return `Press ${shortcutDisplay} to search`;
};
type SearchProps = {
  onOpenChange?: (open: boolean) => void;
  onQueryChange?: () => void;
  onEmptyEscapeKeyDown?: () => void;
  variant?: 'mobile' | 'desktop';
};
export const Search = React.forwardRef<HTMLInputElement, SearchProps>(({
  variant = 'desktop',
  onOpenChange,
  onQueryChange,
  onEmptyEscapeKeyDown
}, forwardedRef) => {
  const isMobile = variant === 'mobile';
  const router = useRouter();
  const snippetLength = 15;
  const hitsPerPage = 50;
  const inputRef = React.useRef<HTMLInputElement>(null);
  const formRef = React.useRef<HTMLFormElement>(null);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [searchState, setSearchState] = React.useState<AutocompleteState>({
    collections: [],
    completion: null,
    context: {},
    isOpen: false,
    query: '',
    activeItemId: null,
    status: 'idle'
  });
  const [shortcutDisplay, setShortcutDisplay] = React.useState('⌘K');
  React.useEffect(() => {
    setShortcutDisplay(getSearchShortcutDisplay());
  }, []);
  const autocomplete = React.useMemo(() => createAutocomplete<SearchItem, React.BaseSyntheticEvent, React.MouseEvent, React.KeyboardEvent>({
    // Provide deterministic id to prevent client / server mismatch warning
    id: 'workos-autocomplete',
    // Always highlight the first result
    defaultActiveItemId: 0,
    placeholder: 'Search the docs…',
    openOnFocus: false,
    debug: false,
    shouldPanelOpen: ({
      state
    }) => Boolean(state.query),
    onStateChange: ({
      state
    }) => {
      setSearchState(state);
      onOpenChange?.(state.isOpen);
    },
    navigator: {
      navigate: ({
        itemUrl
      }) => router.push(itemUrl)
    },
    getSources: ({
      query,
      setStatus,
      state
    }) => {
      const localStorageLanguage = window.localStorage.getItem('@workos/language') || DEFAULT_LANGUAGE;
      if (!query) {
        return [];
      }
      return searchClient.search<SearchItem>([{
        indexName: process.env.NEXT_PUBLIC_ALGOLIA_API_REFERENCE_INDEX || 'dev_api_reference',
        query,
        params: {
          filters: `language:${localStorageLanguage}`,
          hitsPerPage: 10,
          attributesToRetrieve: ['language', 'title', 'slug', 'type'],
          attributesToSnippet: [`title:${snippetLength}`],
          snippetEllipsisText: '…',
          highlightPreTag: '__aa-highlight__',
          highlightPostTag: '__/aa-highlight__'
        }
      }, {
        indexName: process.env.NEXT_PUBLIC_ALGOLIA_INDEX || '',
        query,
        params: {
          hitsPerPage,
          attributesToRetrieve: ['type', 'url', 'url_without_anchor', 'anchor', 'hierarchy.lvl0', 'hierarchy.lvl1', 'hierarchy.lvl2', 'hierarchy.lvl3', 'hierarchy.lvl4', 'content'],
          attributesToSnippet: [`hierarchy.lvl0:${snippetLength}`, `hierarchy.lvl1:${snippetLength}`, `hierarchy.lvl2:${snippetLength}`, `hierarchy.lvl3:${snippetLength}`, `hierarchy.lvl4:${snippetLength}`, `content:${snippetLength}`],
          snippetEllipsisText: '…',
          highlightPreTag: '__aa-highlight__',
          highlightPostTag: '__/aa-highlight__'
        }
      }]).catch(error => {
        // The Algolia `RetryError` happens when all the servers have
        // failed, meaning that there's no chance the response comes
        // back. This is the right time to display an error.
        // See https://github.com/algolia/algoliasearch-client-javascript/blob/2ffddf59bc765cd1b664ee0346b28f00229d6e12/packages/transporter/src/errors/createRetryError.ts#L5
        if (error.name === 'RetryError') {
          setStatus('error');
        }
        throw error;
      }).then(({
        results
      }) => {
        onQueryChange?.();

        // make sure we scroll back up to the top when new results come in.
        if (state.isOpen && scrollRef.current) {
          scrollRef.current.scrollTop = 0;
        }

        // Introducing lint rule banning type assertions
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const apiReferenceHits = (results[0]?.hits || []) as ApiReferenceItem[];
        // Introducing lint rule banning type assertions
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const docsHits = (results[1]?.hits || []) as DocsItem[];
        if (!apiReferenceHits && !docsHits) {
          return [];
        }
        const sources: AutocompleteSource[] = [];
        const apiReferenceSource: ApiReferenceSource = {
          sourceId: 'api-reference',
          onSelect: params => params.setIsOpen(false),
          getItemUrl: ({
            item
          }) => buildApiReferenceResultUrl(item),
          getItems: () => apiReferenceHits
        };
        sources.push(apiReferenceSource);
        const docsResultsByLevel = groupBy(docsHits, hit => hit.hierarchy.lvl0);
        Object.entries(docsResultsByLevel).forEach(([lvl0, items]) => {
          const docsLevelSource: DocsSource = {
            sourceId: `docs.${lvl0}`,
            onSelect: params => {
              // dismiss keyboard on mobile
              if (isMobile && inputRef.current) {
                inputRef.current.blur();
              }
              params.setIsOpen(false);
            },
            getItemUrl: ({
              item
            }) => buildDocsResultUrl(item),
            getItems: () => items
          };
          sources.push(docsLevelSource);
        });

        // Introducing lint rule banning type assertions
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        return sources as InternalAutocompleteSource<SearchItem>[];
      });
    }
  }), [hitsPerPage, snippetLength, onOpenChange, router, onQueryChange, isMobile]);

  // Rerun update callback when input is cleared
  React.useEffect(() => {
    if (searchState.query === '') {
      onQueryChange?.();
    }
  }, [onQueryChange, searchState.query]);

  // Reset autocomplete state when transitioning between mobile and desktop
  // This avoids persisting global layout changes when switching
  const {
    setIsOpen
  } = autocomplete;
  React.useEffect(() => {
    // Match --md
    const mediaQueryList = window.matchMedia('(min-width: 1024px)');
    const handleChange = () => {
      formRef.current?.reset();
      setIsOpen(false);
    };
    mediaQueryList.addEventListener('change', handleChange);
    return () => mediaQueryList.removeEventListener('change', handleChange);
  }, [setIsOpen]);

  // Keyboard shortcut to focus search
  React.useEffect(() => {
    const handleKeydown = (event: KeyboardEvent) => {
      if (!isEditingContent(event)) {
        const key = 'k';
        const metaKey = isMac();
        const ctrlKey = !isMac();
        if (event.key.toLowerCase() === key.toLowerCase() && event.metaKey === metaKey && event.ctrlKey === ctrlKey && !event.shiftKey && !event.altKey) {
          inputRef.current?.focus();
          event.preventDefault();
        }
      }
    };
    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, []);

  // dismiss mobile keyboard when scrolling
  React.useEffect(() => {
    const onTouchMove = (event: TouchEvent) => {
      const input = inputRef.current;
      if (input && input === document.activeElement && event.target !== input) {
        input.blur();
      }
    };
    window.addEventListener('touchmove', onTouchMove);
    return () => {
      window.removeEventListener('touchmove', onTouchMove);
    };
  }, []);
  const inputProps = autocomplete.getInputProps({
    inputElement: inputRef.current
  });
  return <Box position="relative" {...autocomplete.getRootProps({})}>
        <form ref={formRef} className="SearchForm" {...autocomplete.getFormProps({
      inputElement: inputRef.current
    })}>
          <label {...autocomplete.getLabelProps({})}>
            <VisuallyHidden>
              {getSearchShortcutMessage(shortcutDisplay)}.
            </VisuallyHidden>
          </label>

          <TextField.Root ref={composeRefs(inputRef, forwardedRef)} className="SearchField" size={isMobile ? '3' : '2'} variant="soft" {...inputProps} autoCapitalize="none" onKeyDown={event => {
        inputProps.onKeyDown(event);
        if (event.key === 'Escape' && searchState.query === '') {
          onEmptyEscapeKeyDown?.();
        }
      }}>
            <TextField.Slot color="gray" side="left">
              <MagnifyingGlassIcon height="16" width="16" />
            </TextField.Slot>

            <TextField.Slot side="right">
              {searchState.query ?
          // show the clear button when there's a query in the input
          <Tooltip content="Clear">
                  <IconButton size="1" type="reset">
                    <Cross2Icon height="16" width="16" />
                  </IconButton>
                </Tooltip> : !isMobile &&
          // show the keyboard shortcut info when there's no query in the input
          <Box m="-2px">
                    <Tooltip content={getSearchShortcutMessage(shortcutDisplay)}>
                      <Kbd asChild>
                        <button
                // we can make it unreachable via keyboard as we have the same message for the SR label
                tabIndex={-1} onClick={() => {
                  requestAnimationFrame(() => {
                    if (inputRef.current) {
                      inputRef.current.focus();
                    }
                  });
                }}>
                          {shortcutDisplay}
                        </button>
                      </Kbd>
                    </Tooltip>
                  </Box>}
            </TextField.Slot>
          </TextField.Root>
        </form>
        {searchState.isOpen && <RemoveScroll allowPinchZoom as={Slot} enabled={!isMobile}>
            <DismissableLayer.Root asChild disableOutsidePointerEvents={!isMobile} onPointerDownOutside={event => {
        // Introducing lint rule banning type assertions
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const target = event.target as HTMLElement;
        const isPointerDownInForm = formRef.current?.contains(target);
        if (!isMobile && !isPointerDownInForm) {
          autocomplete.setIsOpen(false);
        }
      }}>
              <div className="SearchPanel" {...autocomplete.getPanelProps({})}>
                <FadedBottomScroll ref={scrollRef} bodyClassName="SearchResultsBody" className="SearchResultsScroll">
                  <SearchResults autocomplete={autocomplete} searchState={searchState} />
                </FadedBottomScroll>
              </div>
            </DismissableLayer.Root>
          </RemoveScroll>}
      </Box>;
});
Search.displayName = 'Search';
type SearchResultsProps = {
  searchState: AutocompleteState;
  autocomplete: AutocompleteApi;
};
const SearchResults = React.memo(({
  searchState,
  autocomplete
}: SearchResultsProps) => {
  const hasResults = searchState.collections.some(collection => collection.items.length > 0);
  if (searchState.status === 'error') {
    return <ItemTitle as="p" mb="2" ml="3" mt="2">
          <ExclamationTriangleIcon style={{
        display: 'inline-block',
        marginRight: 'var(--space-2)',
        marginTop: -1,
        verticalAlign: 'middle'
      }} />
          <Mark>Unable to fetch results.</Mark> You might want to check your
          network connection.
        </ItemTitle>;
  }
  if (!hasResults) {
    return <ItemTitle as="p" m="2">
          No results for <Mark>“{searchState.query}”</Mark>
        </ItemTitle>;
  }
  return <>
        {searchState.collections.filter(collection => collection.items.length > 0).map((collection, index) => <React.Fragment key={collection.source.sourceId}>
              {index > 0 && <hr style={{
        marginTop: 'var(--space-2)',
        marginBottom: 'var(--space-2)',
        height: '1px',
        backgroundColor: 'var(--gray-6)',
        border: 'none'
      }} />}

              <section>
                <ul className="SearchResults" {...autocomplete.getListProps()}>
                  {collection.items.map(item => <li key={item.objectID} className="SearchResultsItem" {...autocomplete.getItemProps({
            item,
            source: collection.source
          })}>
                      {collection.source.sourceId === 'api-reference' ?
            // Introducing lint rule banning type assertions
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            <ApiReferenceItemLink item={item as ApiReferenceItem} /> :
            // Introducing lint rule banning type assertions
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            <DocsItemLink item={item as DocsItem} />}
                    </li>)}
                </ul>
              </section>
            </React.Fragment>)}
      </>;
},
// We don't update the results when Autocomplete is loading or stalled to
// avoid UI flashes:
//  - Empty → Results
//  - NoResults → NoResults with another query
(_prevProps, nextProps) => nextProps.searchState.status === 'loading' || nextProps.searchState.status === 'stalled');
SearchResults.displayName = 'SearchResults';
const ApiReferenceItemLink = ({
  item
}: {
  item: ApiReferenceItem;
}) => {
  const url = buildApiReferenceResultUrl(item);
  const isExternal = isExternalUrl(url);
  return <Flex align="center" gap="2" justify="between" p="2" data-sentry-element="Flex" data-sentry-component="ApiReferenceItemLink" data-sentry-source-file="search.tsx">
      <Flex asChild gap="2" data-sentry-element="Flex" data-sentry-source-file="search.tsx">
        <NextLink className="rt-reset" href={url} rel={isExternal ? 'noreferrer' : undefined} target={isExternal ? '_blank' : undefined} data-sentry-element="NextLink" data-sentry-source-file="search.tsx">
          {item.language === 'curl' && item.type && <HttpMethodBadge method={item.type}>
              <Code variant="ghost">{item.type}</Code>
            </HttpMethodBadge>}

          {item.sendsEmail && <Badge>Sends email</Badge>}

          <Code size="2" style={{
          whiteSpace: 'pre-line'
        }} variant="ghost" data-sentry-element="Code" data-sentry-source-file="search.tsx">
            <Highlight attribute="title" hit={item} data-sentry-element="Highlight" data-sentry-source-file="search.tsx" />
          </Code>
        </NextLink>
      </Flex>
      {isExternal && <ExternalLinkIcon />}
    </Flex>;
};
const DocsItemLink = ({
  item
}: {
  item: DocsItem;
}) => {
  const url = buildDocsResultUrl(item);
  return <NextLink href={url} style={{
    display: 'block',
    padding: 'var(--space-2)',
    textDecoration: 'none',
    color: 'inherit'
  }} data-sentry-element="NextLink" data-sentry-component="DocsItemLink" data-sentry-source-file="search.tsx">
      <ItemTitle as="p" data-sentry-element="ItemTitle" data-sentry-source-file="search.tsx">
        <Highlight hit={item} attribute={item.type === 'content' ? 'content' : ['hierarchy', item.type]} data-sentry-element="Highlight" data-sentry-source-file="search.tsx" />
      </ItemTitle>
      {/* Adding a semi-colon to insert a break in the speech flow */}
      <VisuallyHidden data-sentry-element="VisuallyHidden" data-sentry-source-file="search.tsx">; </VisuallyHidden>
      <ItemBreadcrumb item={item} levels={SUPPORTED_LEVELS} data-sentry-element="ItemBreadcrumb" data-sentry-source-file="search.tsx" />
    </NextLink>;
};
const ItemTitle: React.FC<React.ComponentPropsWithoutRef<typeof Text>> = props => <Text truncate size="2" style={{
  lineHeight: 1.2
}} {...props} data-sentry-element="Text" data-sentry-component="ItemTitle" data-sentry-source-file="search.tsx" />;
function ItemBreadcrumb({
  item,
  levels
}: {
  item: DocsItem;
  levels: typeof SUPPORTED_LEVELS;
}) {
  const itemLevelIndex = item.type === 'content' ? levels.length - 1 : levels.indexOf(item.type);
  const breadcrumbLevels = levels.slice(0, itemLevelIndex);
  const hasBreadcrumbs = breadcrumbLevels.length > 0;
  return hasBreadcrumbs ? <Text as="p" color="gray" mt="1" size="1" wrap="wrap" data-sentry-element="Text" data-sentry-component="ItemBreadcrumb" data-sentry-source-file="search.tsx">
      {breadcrumbLevels.map((level, index) => {
      const heading = item.hierarchy[level];
      return heading ? <React.Fragment key={index}>
            {index > 0 ? <>
                <CaretRightIcon style={{
            color: 'var(--gray-a9)',
            display: 'inline-block',
            verticalAlign: 'middle',
            marginTop: -2
          }} />
                {/* Adding a comma to insert a natural break in the speech flow */}
                <VisuallyHidden>, </VisuallyHidden>
              </> : null}
            {item.hierarchy[level]}
          </React.Fragment> : null;
    })}
    </Text> : null;
}
const Highlight = <THit extends object,>({
  hit,
  attribute
}: {
  hit: THit;
  attribute: keyof THit | string[];
}) => <>
    {parseAlgoliaHitSnippet<THit>({
    hit,
    attribute
  }).map(({
    value,
    isHighlighted
  }, index) => isHighlighted ? <Mark key={index}>{value}</Mark> : value)}
  </>;
const Mark: React.FC<{
  children?: React.ReactNode;
}> = ({
  children
}) => <mark style={{
  backgroundColor: 'transparent',
  color: 'inherit',
  fontWeight: 'bold'
}} data-sentry-component="Mark" data-sentry-source-file="search.tsx">
    {children}
  </mark>;

// ----------------------------------------------------------------------------

const groupBy = <TValue extends Record<string, unknown>,>(values: TValue[], predicate: (value: TValue) => string): Record<string, TValue[]> => values.reduce<Record<string, TValue[]>>((acc, item) => {
  const key = predicate(item);
  if (!acc.hasOwnProperty(key)) {
    acc[key] = [];
  }
  const accKey = acc[key];
  if (accKey) {
    accKey.push(item);
  }
  return acc;
}, {});
function isEditingContent(event: KeyboardEvent) {
  // Introducing lint rule banning type assertions
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const element = event.target as HTMLElement;
  const tagName = element.tagName;
  return element.isContentEditable || ['INPUT', 'SELECT', 'TEXTAREA'].includes(tagName);
}
const buildApiReferenceResultUrl = (item: ApiReferenceItem) => item.slug;
function buildDocsResultUrl(item: DocsItem) {
  if (!item.anchor) {
    // The Algolia scraper indexes absolute urls but we need to pass relative
    // paths to the router in order for next.js to correctly prefetch resources
    const match = item.url.match(/.*\/docs\/([^#]*)/);
    const slug = match?.[1];
    if (slug) {
      return '/' + slug;
    }
    return item.url;
  }

  // Handle Radix UI generated anchors that aren't useful for navigation
  try {
    // Check if anchor is a Radix UI generated ID (pattern: radix-:xxxxx: or just :xxxxx:)
    if (item.anchor.match(/^radix-:R[a-zA-Z0-9]+:$/) || item.anchor.match(/^:R[a-zA-Z0-9]+:$/)) {
      // This is a useless Radix-generated anchor, fall back to the page URL
      const match = item.url.match(/.*\/docs\/([^#]*)/);
      const slug = match?.[1];
      if (slug) {
        return '/' + slug;
      }
      return item.url;
    }

    // If the anchor is a valid relative path or fragment, use it
    if (item.anchor.startsWith('/') || item.anchor.startsWith('#')) {
      return item.anchor;
    }

    // For any other malformed anchor, fall back to generating the page URL
    const match = item.url.match(/.*\/docs\/([^#]*)/);
    const slug = match?.[1];
    if (slug) {
      return '/' + slug;
    }
    return item.url;
  } catch {
    // If any error occurs, fall back to the page URL
    const match = item.url.match(/.*\/docs\/([^#]*)/);
    const slug = match?.[1];
    if (slug) {
      return '/' + slug;
    }
    return item.url;
  }
}